// src/Dashboard/pages/user/Inscription/utils/memberCardPdf.js

import jsPDF from "jspdf";

import {
  formatShortDateFR,
  getAdherentAvatar,
  getLicenceInsuranceUrl,
  isReglementValidated,
  toAbsoluteUrl,
} from "./inscriptionUtils";

const DESIGN = {
  width: 920,
  height: 580,
};

const CARD_SIZE = {
  widthMm: 85.6,
  heightMm: 54,
};

const MM_TO_PT = 72 / 25.4;

const COLORS = {
  navy: [2, 6, 23],
  gold: [234, 179, 8],
  goldLight: [250, 204, 21],
  white: [255, 255, 255],
  bg: [248, 250, 252],
  slate50: [248, 250, 252],
  slate100: [241, 245, 249],
  slate200: [226, 232, 240],
  slate300: [203, 213, 225],
  slate500: [100, 116, 139],
  slate700: [51, 65, 85],
  emerald: [5, 150, 105],
  emeraldLight: [236, 253, 245],
  emeraldBorder: [167, 243, 208],
  gray: [148, 163, 184],
};

const cleanFileName = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();

const getClubName = (settings = {}) =>
  settings?.nom_club ||
  settings?.club_name ||
  settings?.association_name ||
  settings?.nom_association ||
  settings?.structure_name ||
  "Club sportif";

const getSeason = ({ adherent, activeSeason, settings }) =>
  adherent?.saison_active ||
  activeSeason ||
  settings?.saison_active ||
  "—";

const getFileName = ({ adherent, activeSeason, settings }) => {
  const season = getSeason({
    adherent,
    activeSeason,
    settings,
  });

  return `carte-membre-${cleanFileName(
    adherent?.prenom || "adherent",
  )}-${cleanFileName(adherent?.nom || "")}-${cleanFileName(season)}.pdf`;
};

const getMimeFromDataUrl = (dataUrl = "") => {
  if (dataUrl.startsWith("data:image/png")) return "PNG";
  if (dataUrl.startsWith("data:image/webp")) return "WEBP";

  return "JPEG";
};

const loadImageAsDataUrl = async (url) => {
  const absoluteUrl = toAbsoluteUrl(url || "");

  if (!absoluteUrl) return "";
  if (absoluteUrl.startsWith("data:")) return absoluteUrl;

  try {
    const response = await fetch(absoluteUrl, {
      mode: "cors",
      credentials: "omit",
    });

    if (!response.ok) return "";

    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;

      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn(
      "Impossible de charger l’image pour le PDF :",
      absoluteUrl,
      error,
    );

    return "";
  }
};

const loadImageDimensions = async (dataUrl) => {
  if (!dataUrl) return null;

  return await new Promise((resolve) => {
    const image = new Image();

    image.onload = () => {
      resolve({
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      });
    };

    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
};

export const generateMemberCardPdf = async ({
  adherent,
  dossier,
  settings,
  activeSeason,
  mode = "save",
  targetWindow = null,
}) => {
  if (!adherent) {
    throw new Error(
      "Aucun adhérent fourni pour générer la carte membre.",
    );
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: [
      CARD_SIZE.widthMm * MM_TO_PT,
      CARD_SIZE.heightMm * MM_TO_PT,
    ],
    compress: true,
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const scale = Math.min(
    pageWidth / DESIGN.width,
    pageHeight / DESIGN.height,
  );

  const cardWidth = DESIGN.width * scale;
  const cardHeight = DESIGN.height * scale;

  const offsetX = (pageWidth - cardWidth) / 2;
  const offsetY = (pageHeight - cardHeight) / 2;

  const X = (value) => offsetX + value * scale;
  const Y = (value) => offsetY + value * scale;
  const S = (value) => value * scale;

  const clubName = getClubName(settings);

  const season = getSeason({
    adherent,
    activeSeason,
    settings,
  });

  const reglementValidated = isReglementValidated(dossier);

  /*
   * La licence n’utilise plus validate_licence.
   * Le badge indique maintenant si la licence-assurance finale est disponible.
   */
  const licenceInsuranceUrl = getLicenceInsuranceUrl(dossier);
  const licenceInsuranceAvailable = Boolean(licenceInsuranceUrl);

  const photoUrl = getAdherentAvatar(adherent);
  const logoUrl = settings?.logo_club || "";

  const [photoDataUrl, logoDataUrl] = await Promise.all([
    loadImageAsDataUrl(photoUrl),
    loadImageAsDataUrl(logoUrl),
  ]);

  const drawImageCover = async ({
    dataUrl,
    x,
    y,
    w,
    h,
    radius = 0,
    placeholderText = "PHOTO",
  }) => {
    doc.setFillColor(...COLORS.slate100);

    doc.roundedRect(
      X(x),
      Y(y),
      S(w),
      S(h),
      S(radius),
      S(radius),
      "F",
    );

    if (!dataUrl) {
      doc.setTextColor(...COLORS.slate500);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(S(18));

      doc.text(
        placeholderText,
        X(x + w / 2),
        Y(y + h / 2),
        {
          align: "center",
        },
      );

      return;
    }

    const dimensions = await loadImageDimensions(dataUrl);

    if (!dimensions?.width || !dimensions?.height) {
      doc.setTextColor(...COLORS.slate500);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(S(18));

      doc.text(
        placeholderText,
        X(x + w / 2),
        Y(y + h / 2),
        {
          align: "center",
        },
      );

      return;
    }

    const imageRatio = dimensions.width / dimensions.height;
    const boxRatio = w / h;

    let drawW = w;
    let drawH = h;
    let drawX = x;
    let drawY = y;

    if (imageRatio > boxRatio) {
      drawH = h;
      drawW = h * imageRatio;
      drawX = x - (drawW - w) / 2;
    } else {
      drawW = w;
      drawH = w / imageRatio;
      drawY = y - (drawH - h) / 2;
    }

    try {
      doc.addImage(
        dataUrl,
        getMimeFromDataUrl(dataUrl),
        X(drawX),
        Y(drawY),
        S(drawW),
        S(drawH),
      );
    } catch (error) {
      console.warn("Image ignorée dans le PDF :", error);
    }
  };

  const drawStatusBadge = ({
    x,
    y,
    w,
    label,
    checked,
  }) => {
    const backgroundColor = checked
      ? COLORS.emeraldLight
      : COLORS.slate100;

    const borderColor = checked
      ? COLORS.emeraldBorder
      : COLORS.slate200;

    const iconColor = checked
      ? COLORS.emerald
      : COLORS.gray;

    const textColor = checked
      ? COLORS.emerald
      : COLORS.slate500;

    doc.setFillColor(...backgroundColor);
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(S(1));

    doc.roundedRect(
      X(x),
      Y(y),
      S(w),
      S(54),
      S(16),
      S(16),
      "FD",
    );

    doc.setFillColor(...iconColor);
    doc.circle(X(x + 28), Y(y + 27), S(16), "F");

    doc.setTextColor(...COLORS.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(S(16));

    doc.text(
      checked ? "✓" : "×",
      X(x + 28),
      Y(y + 33),
      {
        align: "center",
      },
    );

    doc.setTextColor(...textColor);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(S(14));

    doc.text(
      label,
      X(x + 54),
      Y(y + 32),
      {
        maxWidth: S(w - 70),
      },
    );
  };

  const drawField = ({
    x,
    y,
    label,
    value,
    boxed = false,
  }) => {
    doc.setTextColor(...COLORS.slate700);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(S(18));

    doc.text(label, X(x), Y(y));

    doc.setTextColor(...COLORS.navy);
    doc.setFont("helvetica", "bold");

    if (boxed) {
      doc.setFillColor(...COLORS.white);
      doc.setDrawColor(...COLORS.slate300);
      doc.setLineWidth(S(1));

      doc.roundedRect(
        X(x + 188),
        Y(y - 25),
        S(285),
        S(50),
        S(12),
        S(12),
        "FD",
      );

      doc.setFontSize(S(22));

      doc.text(
        String(value || "—"),
        X(x + 202),
        Y(y + 7),
        {
          maxWidth: S(250),
        },
      );
    } else {
      doc.setFontSize(S(26));

      doc.text(
        String(value || "—"),
        X(x + 188),
        Y(y + 8),
        {
          maxWidth: S(260),
        },
      );
    }

    doc.setDrawColor(...COLORS.gold);
    doc.setLineWidth(S(1));

    doc.line(
      X(x),
      Y(y + 22),
      X(x + 472),
      Y(y + 22),
    );
  };

  /* ---------- Fond général ---------- */

  doc.setFillColor(...COLORS.white);

  doc.roundedRect(
    X(0),
    Y(0),
    S(DESIGN.width),
    S(DESIGN.height),
    S(34),
    S(34),
    "F",
  );

  doc.setFillColor(...COLORS.bg);

  doc.roundedRect(
    X(22),
    Y(16),
    S(DESIGN.width - 44),
    S(DESIGN.height - 32),
    S(34),
    S(34),
    "F",
  );

  /* ---------- Décoration ---------- */

  doc.setDrawColor(...COLORS.slate100);
  doc.setLineWidth(S(24));
  doc.circle(X(770), Y(215), S(105), "S");

  doc.setFillColor(...COLORS.slate100);

  doc.roundedRect(
    X(-40),
    Y(160),
    S(110),
    S(290),
    S(55),
    S(55),
    "F",
  );

  doc.setFillColor(...COLORS.navy);

  doc.triangle(
    X(22),
    Y(16),
    X(365),
    Y(16),
    X(220),
    Y(165),
    "F",
  );

  doc.triangle(
    X(22),
    Y(16),
    X(22),
    Y(165),
    X(220),
    Y(165),
    "F",
  );

  doc.setFillColor(...COLORS.gold);

  doc.triangle(
    X(170),
    Y(16),
    X(225),
    Y(16),
    X(150),
    Y(165),
    "F",
  );

  /* ---------- Photo ---------- */

  await drawImageCover({
    dataUrl: photoDataUrl,
    x: 50,
    y: 40,
    w: 340,
    h: 330,
    radius: 26,
  });

  doc.setDrawColor(...COLORS.gold);
  doc.setLineWidth(S(4));

  doc.roundedRect(
    X(50),
    Y(40),
    S(340),
    S(330),
    S(26),
    S(26),
    "S",
  );

  /* ---------- Saison ---------- */

  doc.setFillColor(...COLORS.navy);

  doc.roundedRect(
    X(50),
    Y(390),
    S(340),
    S(52),
    S(20),
    S(20),
    "F",
  );

  doc.setTextColor(...COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(S(16));

  doc.text("SAISON", X(112), Y(424));

  doc.setTextColor(...COLORS.goldLight);
  doc.setFontSize(S(20));

  doc.text(season, X(188), Y(424));

  /* ---------- Badges ---------- */

  drawStatusBadge({
    x: 50,
    y: 456,
    w: 340,
    label: "Règlement validé",
    checked: reglementValidated,
  });

  drawStatusBadge({
    x: 50,
    y: 516,
    w: 340,
    label: "Licence-assurance disponible",
    checked: licenceInsuranceAvailable,
  });

  /* ---------- Titre ---------- */

  doc.setTextColor(...COLORS.navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(S(28));

  doc.text("CARTE DE MEMBRE", X(420), Y(78));

  doc.setDrawColor(...COLORS.gold);
  doc.setLineWidth(S(4));

  doc.line(
    X(420),
    Y(104),
    X(690),
    Y(104),
  );

  /* ---------- Logo ---------- */

  doc.setFillColor(...COLORS.white);
  doc.circle(X(840), Y(90), S(55), "F");

  doc.setDrawColor(...COLORS.slate200);
  doc.setLineWidth(S(2));
  doc.circle(X(840), Y(90), S(55), "S");

  if (logoDataUrl) {
    try {
      doc.addImage(
        logoDataUrl,
        getMimeFromDataUrl(logoDataUrl),
        X(790),
        Y(40),
        S(100),
        S(100),
      );
    } catch (error) {
      console.warn("Logo ignoré dans le PDF :", error);
    }
  }

  /* ---------- Nom du club ---------- */

  doc.setTextColor(...COLORS.navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(S(44));

  const clubLines = doc
    .splitTextToSize(clubName, S(430))
    .slice(0, 2);

  doc.text(
    clubLines,
    X(420),
    Y(175),
    {
      lineHeightFactor: 1.1,
    },
  );

  /* ---------- Bloc informations ---------- */

  const infoY = clubLines.length > 1 ? 280 : 260;

  doc.setFillColor(...COLORS.white);
  doc.setDrawColor(...COLORS.slate100);
  doc.setLineWidth(S(1));

  doc.roundedRect(
    X(420),
    Y(infoY),
    S(480),
    S(255),
    S(24),
    S(24),
    "FD",
  );

  drawField({
    x: 450,
    y: infoY + 48,
    label: "Nom :",
    value: String(adherent?.nom || "—").toUpperCase(),
  });

  drawField({
    x: 450,
    y: infoY + 112,
    label: "Prénom :",
    value: adherent?.prenom || "—",
  });

  drawField({
    x: 450,
    y: infoY + 176,
    label: "Naissance :",
    value: formatShortDateFR(adherent?.date_naissance),
  });

  drawField({
    x: 450,
    y: infoY + 240,
    label: "Licence :",
    value: adherent?.licence_number || "—",
    boxed: true,
  });

  /* ---------- Bordure ---------- */

  doc.setDrawColor(...COLORS.slate200);
  doc.setLineWidth(S(1));

  doc.roundedRect(
    X(22),
    Y(16),
    S(DESIGN.width - 44),
    S(DESIGN.height - 32),
    S(34),
    S(34),
    "S",
  );

  /* ---------- Téléchargement ou impression ---------- */

  const fileName = getFileName({
    adherent,
    activeSeason,
    settings,
  });

  if (mode === "print") {
    doc.autoPrint();

    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);

    if (targetWindow && !targetWindow.closed) {
      targetWindow.location.href = blobUrl;
    } else {
      window.open(
        blobUrl,
        "_blank",
        "noopener,noreferrer",
      );
    }

    return;
  }

  doc.save(fileName);
};